import DialogContentText from "@material-ui/core/DialogContentText";
import { isSuperUser } from "@saleor/auth/misc";
import ActionDialog from "@saleor/components/ActionDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import usePermissionGroupSearch from "@saleor/searches/usePermissionGroupSearch";
import useSearchTenantByBrandId from "@saleor/searches/useSearchTenantByBrandId";
import {
  LockStatus,
  SmopStatusEnum,
  SmopUserTypeCode,
  StaffUpdateInput
} from "@saleor/types/globalTypes";
import { getStaffUserType } from "@saleor/utils/smop";
import { validateUpdateStaffForm } from "@saleor/utils/validators/staff";
import React, { useEffect } from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import OtherStaffResetDialog from "../components/OtherStaffResetDialog";
import StaffDetailsPage, {
  StaffDetailsFormData
} from "../components/StaffDetailsPage/StaffDetailsPage";
import StaffPasswordResetDialog from "../components/StaffPasswordResetDialog";
import { UserType } from "../constants";
import {
  TypedStaffAvatarDeleteMutation,
  TypedStaffAvatarUpdateMutation,
  TypedStaffMemberDeleteMutation,
  TypedStaffMemberUpdateMutation,
  useChangeOtherStaffPassword,
  useChangeStaffPassword
} from "../mutations";
import {
  TypedStaffMemberDetailsQuery,
  useMallIdSearch,
  useStaffBrandAssignmentQuery,
  useStaffMallAssignmentQuery,
  useStaffTenantAssignmentQuery
} from "../queries";
import { AccountSetNewPassword } from "../types/AccountSetNewPassword";
// import { listBrands, listMalls, searchTenantsByBrandId } from "../queries";
import { ChangeStaffPassword } from "../types/ChangeStaffPassword";
import { MallIdSearch_user_malls_edges_node } from "../types/MallIdSearch";
import { StaffAvatarDelete } from "../types/StaffAvatarDelete";
import { StaffAvatarUpdate } from "../types/StaffAvatarUpdate";
import { StaffMemberDelete } from "../types/StaffMemberDelete";
import { StaffMemberUpdate } from "../types/StaffMemberUpdate";
import {
  staffListUrl,
  staffMemberDetailsUrl,
  StaffMemberDetailsUrlQueryParams
} from "../urls";
interface OrderListProps {
  id: string;
  params: StaffMemberDetailsUrlQueryParams;
}

export const StaffDetails: React.FC<OrderListProps> = ({ id, params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const user = useUser();
  const intl = useIntl();
  const shop = useShop();
  const userPermissions = user?.user.userPermissions.map(p => p.code) || [];

  const permissions = (shop?.permissions || []).map(perm => ({
    ...perm,
    disabled: !userPermissions.includes(perm.code)
  }));

  const hasFullAccess = permissions.filter(
    disabled => disabled.disabled === true
  );

  const isSuperAdmin = hasFullAccess.length === 0 ? true : false;

  const closeModal = () =>
    navigate(
      staffMemberDetailsUrl(id, {
        ...params,
        action: undefined
      })
    );

  const handleChangePassword = (data: ChangeStaffPassword) => {
    if (data.passwordChange.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      closeModal();
    }
  };

  const [changePassword, changePasswordOpts] = useChangeStaffPassword({
    onCompleted: handleChangePassword
  });

  const handleChangeOtherStaffPassword = (data: AccountSetNewPassword) => {
    if (data.accountSetNewPassword.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      closeModal();
    }
  };

  const [
    changeOtherPassword,
    changeOtherPasswordOpts
  ] = useChangeOtherStaffPassword({
    onCompleted: handleChangeOtherStaffPassword
  });

  const handleBack = () => navigate(staffListUrl());

  const {
    loadMore: loadMorePermissionGroups,
    search: searchPermissionGroups,
    result: searchPermissionGroupsOpts
  } = usePermissionGroupSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const [selectedPermissionGroup, setSelectedPermissionGroup] = useState("");

  const [showBrands, setShowBrands] = useState(false);
  const [showMalls, setShowMalls] = useState(false);
  const [showTenants, setShowTenants] = useState(false);

  const mallInitialSearchData = {
    after: null,
    first: 100,
    query: "",
    statuses: [SmopStatusEnum.ACT]
  };

  const {
    loadMore: loadMoreMall,
    search: searchMalls,
    result: searchMallsOpts
  } = useMallSearch({
    variables: mallInitialSearchData
  });

  const brandsVariable = {
    after: null,
    first: 20,
    mallId: showTenants ? "TWFsbDow" : null,
    query: " "
  };

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: searchBrandsOpts
  } = useBrandSearch({
    variables: brandsVariable
  });

  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [brandId, setBrandId] = React.useState("");
  const DEFAULT_TENANT_INITIAL_DATA = {
    after: null,
    brand: brandId ? [brandId] : [],
    first: 20,
    query: " ",
    status: SmopStatusEnum.ACT
  };
  const {
    loadMore: loadMoreTenant,
    search: searchTenants,
    result: searchTenantsOpts
  } = useSearchTenantByBrandId({
    variables: DEFAULT_TENANT_INITIAL_DATA
  });

  const malls = maybe(() => searchMallsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const brands = maybe(() => searchBrandsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const tenants = maybe(() => searchTenantsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );

  useEffect(() => {
    const permissionGroup = selectedPermissionGroup?.toUpperCase();
    if ([UserType.STR_OMI_ADMIN].includes(permissionGroup)) {
      // Creating a OMI Admin

      // Hide - Malls & Tenants (Store) Dropdowns
      setShowBrands(false);
      setShowMalls(false);
      setShowTenants(false);
    } else if ([UserType.STR_SELLER_MASTER].includes(permissionGroup)) {
      // Creating a Seller Master
      // Show - Brands dropdown
      // Hide - Malls & Tenants (Store) Dropdowns
      setShowBrands(true);
      setShowMalls(false);
      setShowTenants(false);
    } else if (
      [
        UserType.STR_MALL_ADMIN,
        UserType.STR_OMI_OPS,
        UserType.STR_RIDER,
        UserType.STR_RUNNER
      ].includes(permissionGroup)
    ) {
      // Creating a Mall Admin
      // Show - Malls Dropdown
      // Hide - Brands & Tenants (Store) Dropdowns
      setShowMalls(true);
      setShowBrands(false);
      setShowTenants(false);
    } else if ([UserType.STR_STORE_ADMIN].includes(permissionGroup)) {
      // Creating a Store Admin
      // Show - Brands & Tenants (Store) Dropdowns
      // Hide - Malls Dropdown
      setShowMalls(false);
      setShowBrands(true);
      setShowTenants(true);
    } else {
      // Hide all dropdowns
      setShowMalls(false);
      setShowBrands(false);
      setShowTenants(false);
    }
  }, [selectedPermissionGroup]);

  const handleUserTypeChange = selectedValue => {
    setSelectedPermissionGroup(selectedValue);
  };

  const [formErrors, setFormErrors] = useState([]);

  // useEffect(() => {
  //   if (formErrors.length > 0) {
  //     notify({
  //       status: "error",
  //       text: intl.formatMessage({
  //         defaultMessage: "Invalid Form!"
  //       })
  //     });
  //   }
  // }, [formErrors]);

  const [staffAssignedTenantId, setStaffAssignedTenantId] = useState("");

  useEffect(() => {
    if (staffAssignedTenantId) {
      refetchStaffTenantAssignment();
    }
  }, [staffAssignedTenantId]);

  const {
    data: staffTenantAssignmentData,
    refetch: refetchStaffTenantAssignment
  } = useStaffTenantAssignmentQuery({
    skip: !staffAssignedTenantId,
    variables: {
      id: staffAssignedTenantId
    }
  });

  const [staffAssignedBrandId, setStaffAssignedBrandId] = useState("");

  useEffect(() => {
    if (staffAssignedBrandId) {
      refetchStaffBrandAssignment();
    }
  }, [staffAssignedBrandId]);

  const {
    data: staffBrandAssignmentData,
    refetch: refetchStaffBrandAssignment
  } = useStaffBrandAssignmentQuery({
    skip: !staffAssignedBrandId,
    variables: {
      id: staffAssignedBrandId
    }
  });

  const [staffAssignedMallId, setStaffAssignedMallId] = useState("");

  useEffect(() => {
    if (staffAssignedMallId) {
      refetchStaffMallAssignment();
    }
  }, [staffAssignedBrandId]);

  const {
    data: staffMallAssignmentData,
    refetch: refetchStaffMallAssignment
  } = useStaffMallAssignmentQuery({
    skip: !staffAssignedMallId,
    variables: {
      id: staffAssignedMallId
    }
  });

  const [
    userMallListAfterPagination,
    setUserMallListAfterPagination
  ] = useState("");
  const [stopQuery, setStopQuery] = useState(true);
  const [fetchedMalls, setFetchedMalls] = useState<
    MallIdSearch_user_malls_edges_node[]
  >();
  const [staffID, setStaffID] = useState("");

  const {
    data: userMallsListData,
    loading: userMallListLoading,
    refetch: refetchUserMallsList
  } = useMallIdSearch({
    skip: Boolean(stopQuery),
    variables: { after: userMallListAfterPagination, first: 100, id: staffID }
  });

  const userMallNextPageQuery = userMallsListData?.user?.malls?.pageInfo;
  useEffect(() => {
    if (userMallsListData && !userMallListLoading) {
      setFetchedMalls(current => [
        ...(current ?? []),
        ...(userMallsListData?.user?.malls?.edges.map(edge => edge.node) ?? [])
      ]);
      setUserMallListAfterPagination(userMallNextPageQuery?.endCursor);

      if (
        !userMallNextPageQuery?.hasNextPage &&
        userMallNextPageQuery?.endCursor === null
      ) {
        setUserMallListAfterPagination("");
        setStopQuery(true);
      } else if (userMallNextPageQuery?.endCursor) {
        refetchUserMallsList();
      }
    }
  }, [
    userMallsListData,
    userMallListLoading,
    fetchedMalls,
    refetchUserMallsList,
    userMallNextPageQuery
  ]);

  return (
    <TypedStaffMemberDetailsQuery displayLoader variables={{ first: 100, id }}>
      {({ data, loading, refetch: refetchStaffMemberDetails }) => {
        const staffMember = data?.user;

        useEffect(() => {
          if (data?.user?.malls.pageInfo.endCursor) {
            setEndCursor(data?.user?.malls.pageInfo.endCursor);
          }
        }, [data]);

        useEffect(() => {
          if (staffMember?.id !== undefined) {
            const fetchedUserMalls = maybe(() =>
              userMallsListData?.user?.malls?.edges.map(edges => edges.node)
            );
            setFetchedMalls(fetchedUserMalls);
            setStopQuery(false);
            setStaffID(staffMember?.id);
          }
        }, [staffMember]);

        useEffect(() => {
          if (endCursor) {
            refetchStaffMemberDetails({
              after: endCursor,
              first: 100,
              id
            });
          }
        }, [endCursor, refetchStaffMemberDetails]);

        if (staffMember === null) {
          return <NotFoundPage onBack={handleBack} />;
        }

        const assignedMallName = staffMallAssignmentData?.mall?.name || "-";
        const assignedTenantName =
          staffTenantAssignmentData?.tenant?.storeName || "-";
        const assignedBrandName =
          staffTenantAssignmentData?.tenant?.brand?.name ||
          staffBrandAssignmentData?.brand?.name ||
          "-";
        const assignedCompanyNameByBrandId =
          staffTenantAssignmentData?.tenant?.brand?.legalName ||
          staffBrandAssignmentData?.brand?.legalName ||
          "-";

        // Code for getting assignment details
        const staffMemberUserType = getStaffUserType(staffMember);
        if (staffMemberUserType?.code === SmopUserTypeCode.STORE_ADMIN) {
          const tenant = staffMemberUserType?.accesses.find(
            a => a.accessType === "TENANT"
          );
          if (tenant) {
            setStaffAssignedTenantId(tenant.objectId);
          }
        } else if (
          staffMemberUserType?.code === SmopUserTypeCode.SELLER_MASTER
        ) {
          const brand = staffMemberUserType?.accesses.find(
            a => a.accessType === "BRAND"
          );
          if (brand) {
            setStaffAssignedBrandId(brand.objectId);
          }
        } else if (
          staffMemberUserType?.code === SmopUserTypeCode.MALL_ADMIN ||
          staffMemberUserType?.code === SmopUserTypeCode.OMI_OPS ||
          staffMemberUserType?.code === SmopUserTypeCode.RIDER ||
          staffMemberUserType?.code === SmopUserTypeCode.RUNNER
        ) {
          const mall = staffMemberUserType?.accesses.find(
            a => a.accessType === "MALL"
          );
          if (mall) {
            setStaffAssignedMallId(mall.objectId);
          }
        }

        const handleStaffMemberUpdate = (data: StaffMemberUpdate) => {
          setFormErrors([]);

          if (!maybe(() => data.staffUpdate.errors.length !== 0)) {
            notify({
              status: "success",
              text: intl.formatMessage(commonMessages.savedChanges)
            });
            try {
              localStorage.setItem(
                "coupon_usage_limit_permission_updated",
                Date.now().toString()
              );
            } catch (e) {
              // Silently ignore storage error
            }
            refetchStaffMemberDetails();
            setSelectedPermissionGroup("");
          } else {
            if (data.staffUpdate.errors.some(e => e.code === "UNIQUE")) {
              notify({
                status: "error",
                text: data.staffUpdate.errors[0].message
              });
            } else {
              notify({
                status: "error",
                text: intl.formatMessage({
                  defaultMessage: "Something went wrong. Please try again!"
                })
              });
            }
          }
        };
        const handleStaffMemberDelete = (data: StaffMemberDelete) => {
          if (!maybe(() => data.staffDelete.errors.length !== 0)) {
            notify({
              status: "success",
              text: intl.formatMessage(commonMessages.savedChanges)
            });
            navigate(staffListUrl());
          }
        };
        const handleStaffMemberAvatarUpdate = (data: StaffAvatarUpdate) => {
          if (!maybe(() => data.userAvatarUpdate.errors.length !== 0)) {
            notify({
              status: "success",
              text: intl.formatMessage(commonMessages.savedChanges)
            });
          }
        };
        const handleStaffMemberAvatarDelete = (data: StaffAvatarDelete) => {
          if (!maybe(() => data.userAvatarDelete.errors.length !== 0)) {
            notify({
              status: "success",
              text: intl.formatMessage(commonMessages.savedChanges)
            });
            navigate(staffMemberDetailsUrl(id));
          }
        };

        const fetchTenantsByBrandId = id => {
          setBrandId(id);
        };

        return (
          <TypedStaffMemberUpdateMutation onCompleted={handleStaffMemberUpdate}>
            {(updateStaffMember, updateResult) => {
              const handleSubmit = async (formData: StaffDetailsFormData) => {
                const {
                  brandId,
                  contactNo,
                  email,
                  firstName,
                  lastName,
                  mallId,
                  malls,
                  permissionGroup, // The ID of the newly selected permission group
                  permissionGroups, // A collections of IDs, used for deleting existing permissionGroups and replacing with the new selection
                  tenantId
                } = formData;
                const validate = validateUpdateStaffForm({
                  brandId,
                  contactNo,
                  email,
                  firstName,
                  lastName,
                  mallId,
                  malls,
                  permissionGroup,
                  selectedPermissionGroup,
                  tenantId
                });

                if (!validate.isValid) {
                  setFormErrors(validate.errors);
                  return validate.errors;
                } else {
                  let staffUpdateInput: StaffUpdateInput = {
                    // accessGroup: null,
                    addGroups: [],
                    contactNo: formData.contactNo,
                    email: formData.email,
                    firstName: formData.firstName,
                    isActive: formData.isActive,
                    lastName: formData.lastName,
                    malls: formData.malls,
                    objectId: "",
                    promoCreator: formData.promoCreator,
                    removeGroups: [], // deleting existing permissionGroups
                    smopUsertypeCode: null,
                    status: formData.isActive
                      ? LockStatus.ACTIVE
                      : LockStatus.INACTIVE
                  };
                  if (selectedPermissionGroup) {
                    switch (selectedPermissionGroup.toUpperCase()) {
                      case UserType.STR_OMI_ADMIN:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.OMI_ADMIN
                        };
                        break;
                      case UserType.STR_SELLER_MASTER:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.brandId,
                          smopUsertypeCode: SmopUserTypeCode.SELLER_MASTER
                        };
                        break;
                      case UserType.STR_MALL_ADMIN:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.mallId,
                          smopUsertypeCode: SmopUserTypeCode.MALL_ADMIN
                        };
                        break;
                      case UserType.STR_MARKETING:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.MARKETING
                        };
                        break;
                      case UserType.STR_STORE_ADMIN:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.tenantId,
                          smopUsertypeCode: SmopUserTypeCode.STORE_ADMIN
                        };
                        break;
                      case UserType.STR_OMI_OPS:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.mallId,
                          smopUsertypeCode: SmopUserTypeCode.OMI_OPS
                        };
                        break;
                      case UserType.STR_RIDER:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.mallId,
                          smopUsertypeCode: SmopUserTypeCode.RIDER
                        };
                        break;
                      case UserType.STR_RUNNER:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: formData.mallId,
                          smopUsertypeCode: SmopUserTypeCode.RUNNER
                        };
                        break;
                      case UserType.STR_ACQUISITION:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.ACQUISITION
                        };
                        break;
                      case UserType.STR_ACCOUNT_MANAGER:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.ACCOUNT_MANAGER
                        };
                        break;
                      case UserType.STR_KAMS:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.ACCOUNT_MANAGER
                        };
                        break;
                      case UserType.STR_OMI_FINANCE:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.OMI_FINANCE
                        };
                        break;
                      case UserType.STR_FINANCE_SPECIALIST:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.FINANCE_SPECIALIST
                        };
                        break;
                      case UserType.STR_FINANCE_MANAGER:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.FINANCE_MANAGER
                        };
                        break;
                      case UserType.STR_FINANCE_TREASURY_1:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.FINANCE_TREASURY_1
                        };
                        break;
                      case UserType.STR_FINANCE_TREASURY_2:
                        staffUpdateInput = {
                          ...staffUpdateInput,
                          objectId: null,
                          smopUsertypeCode: SmopUserTypeCode.FINANCE_TREASURY_2
                        };
                        break;
                      default:
                        // We only assign values to "objectId" & "smopUsertypeCode" for User Types (1)OMI Admin, (2)Seller Master, (3)Mall Admin & (4)Store Admin
                        // We delete "objectId" & "smopUsertypeCode" properties below as a requirement from the backend
                        delete staffUpdateInput.objectId;
                        delete staffUpdateInput.smopUsertypeCode;
                        break;
                    }
                  }

                  if (permissionGroup) {
                    const isPermissionGroupExisting = permissionGroups.find(
                      p => p === permissionGroup
                    );
                    if (!isPermissionGroupExisting) {
                      staffUpdateInput = {
                        ...staffUpdateInput,
                        addGroups: [permissionGroup],
                        removeGroups: permissionGroup ? permissionGroups : []
                      };
                    }
                  }

                  const result = await updateStaffMember({
                    variables: {
                      first: 100,
                      id,
                      input: staffUpdateInput
                    }
                  });

                  if (result?.data?.staffUpdate?.errors?.length > 0) {
                    setFormErrors(result.data.staffUpdate.errors);
                  }

                  return result?.data?.staffUpdate?.errors || [];
                }
              };
              // console.log(user)
              return (
                <TypedStaffMemberDeleteMutation
                  variables={{ id }}
                  onCompleted={handleStaffMemberDelete}
                >
                  {(deleteStaffMember, deleteResult) => (
                    <TypedStaffAvatarUpdateMutation
                      onCompleted={handleStaffMemberAvatarUpdate}
                    >
                      {updateStaffAvatar => (
                        <TypedStaffAvatarDeleteMutation
                          onCompleted={handleStaffMemberAvatarDelete}
                        >
                          {(deleteStaffAvatar, deleteAvatarResult) => {
                            const isUserSameAsViewer =
                              user.user?.id === data?.user?.id;
                            const isOtherUser =
                              (isSuperUser &&
                                data?.user?.accessGroup?.smopUsertypeCode ===
                                  "STORE_ADMIN") ||
                              data?.user?.accessGroup?.smopUsertypeCode ===
                                "SELLER_MASTER" ||
                              data?.user?.accessGroup?.smopUsertypeCode ===
                                "OMI_ADMIN" ||
                              data?.user?.accessGroup?.smopUsertypeCode ===
                                "OMI_OPS";

                            return (
                              <>
                                <WindowTitle
                                  title={getStringOrPlaceholder(
                                    staffMember?.email
                                  )}
                                />
                                <StaffDetailsPage
                                  staffMemberMalls={fetchedMalls}
                                  mallChoices={malls}
                                  brandChoices={brands}
                                  tenantChoices={tenants}
                                  fetchMalls={query => searchMalls(query)}
                                  fetchBrands={query => searchBrands(query)}
                                  fetchTenants={query => searchTenants(query)}
                                  fetchTenantsByBrandId={fetchTenantsByBrandId}
                                  errors={
                                    formErrors ||
                                    updateResult?.data?.staffUpdate?.errors
                                  }
                                  staffMemberUserType={staffMemberUserType}
                                  assignedMallName={assignedMallName}
                                  assignedTenantName={assignedTenantName}
                                  assignedBrandName={assignedBrandName}
                                  assignedCompanyNameByBrandId={
                                    assignedCompanyNameByBrandId
                                  }
                                  canEditAvatar={false}
                                  // canEditPermission={isSuperAdmin}
                                  canChangePassword={isUserSameAsViewer}
                                  canChangeOtherStaffPassword={isOtherUser}
                                  // canEditStatus={isSuperAdmin}
                                  canRemove={
                                    !isUserSameAsViewer && isSuperAdmin
                                  }
                                  isUserSameAsViewer={isUserSameAsViewer}
                                  disabled={loading}
                                  onBack={handleBack}
                                  initialSearch=""
                                  onChangePassword={() =>
                                    navigate(
                                      staffMemberDetailsUrl(id, {
                                        action: "change-password"
                                      })
                                    )
                                  }
                                  onChangeOtherPw={() =>
                                    navigate(
                                      staffMemberDetailsUrl(id, {
                                        action: "change-other-password"
                                      })
                                    )
                                  }
                                  onDelete={() =>
                                    navigate(
                                      staffMemberDetailsUrl(id, {
                                        action: "remove"
                                      })
                                    )
                                  }
                                  onSubmit={data => handleSubmit(data)}
                                  onImageUpload={file =>
                                    updateStaffAvatar({
                                      variables: {
                                        image: file
                                      }
                                    })
                                  }
                                  onImageDelete={() =>
                                    navigate(
                                      staffMemberDetailsUrl(id, {
                                        action: "remove-avatar"
                                      })
                                    )
                                  }
                                  availablePermissionGroups={searchPermissionGroupsOpts.data?.search.edges.map(
                                    edge => edge.node
                                  )}
                                  staffMember={staffMember}
                                  saveButtonBarState={updateResult.status}
                                  fetchMorePermissionGroups={{
                                    hasMore:
                                      searchPermissionGroupsOpts.data?.search
                                        .pageInfo.hasNextPage,
                                    loading: searchPermissionGroupsOpts.loading,
                                    onFetchMore: loadMorePermissionGroups
                                  }}
                                  onSearchChange={searchPermissionGroups}
                                  fetchMoreMalls={{
                                    hasMore: maybe(
                                      () =>
                                        searchMallsOpts.data.search.pageInfo
                                          .hasNextPage
                                    ),
                                    loading: searchMallsOpts.loading,
                                    onFetchMore: loadMoreMall
                                  }}
                                  fetchMoreBrands={{
                                    hasMore: maybe(
                                      () =>
                                        searchBrandsOpts.data.search.pageInfo
                                          .hasNextPage
                                    ),
                                    loading: searchBrandsOpts.loading,
                                    onFetchMore: loadMoreBrand
                                  }}
                                  fetchMoreTenants={{
                                    hasMore: maybe(
                                      () =>
                                        searchTenantsOpts.data.search.pageInfo
                                          .hasNextPage
                                    ),
                                    loading: searchTenantsOpts.loading,
                                    onFetchMore: loadMoreTenant
                                  }}
                                  handleUserTypeChange={handleUserTypeChange}
                                  // fetchTenantsByBrandId={fetchTenantsByBrandId}
                                  showBrands={showBrands}
                                  showMalls={showMalls}
                                  setEndCursor={setEndCursor}
                                  showTenants={showTenants}
                                />
                                <ActionDialog
                                  open={params.action === "remove"}
                                  title={intl.formatMessage({
                                    defaultMessage: "delete Staff User",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={deleteResult.status}
                                  variant="delete"
                                  onClose={closeModal}
                                  onConfirm={deleteStaffMember}
                                >
                                  <DialogContentText>
                                    <FormattedMessage
                                      defaultMessage="Are you sure you want to delete {email} from staff members?"
                                      values={{
                                        email: getStringOrPlaceholder(
                                          data?.user?.email
                                        )
                                      }}
                                    />
                                  </DialogContentText>
                                </ActionDialog>
                                <ActionDialog
                                  open={params.action === "remove-avatar"}
                                  title={intl.formatMessage({
                                    defaultMessage: "Delete Staff User Avatar",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={deleteAvatarResult.status}
                                  variant="delete"
                                  onClose={closeModal}
                                  onConfirm={deleteStaffAvatar}
                                >
                                  <DialogContentText>
                                    <FormattedMessage
                                      defaultMessage="Are you sure you want to remove {email} avatar?"
                                      values={{
                                        email: (
                                          <strong>
                                            {getStringOrPlaceholder(
                                              data?.user?.email
                                            )}
                                          </strong>
                                        )
                                      }}
                                    />
                                  </DialogContentText>
                                </ActionDialog>
                                <StaffPasswordResetDialog
                                  confirmButtonState={changePasswordOpts.status}
                                  errors={
                                    changePasswordOpts?.data?.passwordChange
                                      ?.errors || []
                                  }
                                  open={params.action === "change-password"}
                                  onClose={closeModal}
                                  onSubmit={data =>
                                    changePassword({
                                      variables: data
                                    })
                                  }
                                />
                                <OtherStaffResetDialog
                                  confirmButtonState={
                                    changeOtherPasswordOpts.status
                                  }
                                  errors={
                                    changeOtherPasswordOpts?.data
                                      ?.accountSetNewPassword?.errors || []
                                  }
                                  userId={id}
                                  open={
                                    params.action === "change-other-password"
                                  }
                                  onClose={closeModal}
                                  onSubmit={data =>
                                    changeOtherPassword({
                                      variables: data
                                    })
                                  }
                                />
                              </>
                            );
                          }}
                        </TypedStaffAvatarDeleteMutation>
                      )}
                    </TypedStaffAvatarUpdateMutation>
                  )}
                </TypedStaffMemberDeleteMutation>
              );
            }}
          </TypedStaffMemberUpdateMutation>
        );
      }}
    </TypedStaffMemberDetailsQuery>
  );
};

export default StaffDetails;
